import { useMemo } from "react";

import { observer } from "mobx-react-lite";
import { NextIntlClientProvider, useTranslations } from "next-intl";

import { getBoundaryClassNames } from "@/components/expression/utils/getBoundaryClassNames";
import { HintExpression } from "@/components/expression/utils/HintExpression";
import { Icon } from "@/components/icons/Icon";
import { trackedHintType } from "@/components/suggestions/types";
import { nodeIsExpression } from "@/components/tiptap/tiptap-expression/utils";
import { acceptPhantomHint } from "@/input/utils/editOperators";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";
import { getIsMobile } from "@/utils/getIsMobile";
import { twMerge } from "@/utils/twMerge";

import messages from "../../../../../messages/en.json";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeJson } from "@corca/data";
import type { EditorState } from "@tiptap/pm/state";

type PhantomHintWrapperProps = {
  node: NodeJson;
  doc: DocumentModel;
  getPos: () => number | undefined;
  pmState: EditorState;
};

const PhantomHintWrapper = observer(function PhantomHintWrapper({
  node,
  doc,
  getPos,
  pmState,
}: PhantomHintWrapperProps) {
  const t = useTranslations("phantomHint");

  const pos = getPos();

  const showExplainer = useMemo(() => {
    if (pos !== pmState.selection.head) return false;
    const $head = pmState.doc.resolve(pos);
    for (let d = $head.depth; d > 0; d -= 1) {
      const { name } = $head.node(d).type;
      if (name === "paragraph") continue;
      if ($head.node(d).type.isBlock) return false;
    }
    return $head.nodeAfter?.type.name !== "text";
  }, [pmState, pos]);

  // A hint trailing a non-empty math node (inline "= result") reuses that node's left boundary and
  // draws only its own right one. Standing alone (an empty table cell) it must draw both.
  const hasLeftMathNeighbor = useMemo(() => {
    if (!pos) return false;
    const before = pmState.doc.resolve(pos).nodeBefore;
    if (!nodeIsExpression(before)) return false;
    const wrapper = doc.nodes.get(String(before?.attrs.nodeId ?? ""));
    return !!(wrapper?.is(WrapperNodeModel) && (wrapper.inlineNode?.nodes.length ?? 0) > 0);
  }, [pmState, doc, pos]);

  const { phantomHintIndex, phantomHintSize, allHintsResolved, previewHintShown, phantomHint } =
    doc.editor.hints;

  const keyClass = "rounded border border-gray-300 dark:border-gray-700 px-1";

  // hint controls need more design work & better handling of async suggestion resolution
  const showHintControls = false; // phantomHintSize > 1;

  const handleAcceptClick = () => {
    acceptPhantomHint(doc, node.children, trackedHintType(phantomHint));
  };

  return (
    <div
      className={twMerge(
        "relative inline-flex items-baseline",
        getBoundaryClassNames(hasLeftMathNeighbor ? "right" : true),
      )}
    >
      <HintExpression
        node={node}
        className={twMerge(
          "pr-[4px]",
          // Overlap the neighbour's right boundary when trailing one; otherwise pad symmetrically.
          hasLeftMathNeighbor ? "-ml-[4px]" : "pl-[4px]",
        )}
      />
      {showExplainer && !previewHintShown && (
        <div className="flex items-center relative before:content-zero-space">
          <div className="absolute whitespace-nowrap flex items-center gap-2 text-gray-400 dark:text-gray-600 text-size-13 pl-16 pr-4 py-1 [.line-wrapper:hover_&]:bg-gray-50 dark:[.line-wrapper:hover_&]:bg-gray-950">
            {getIsMobile() ? (
              <button
                type="button"
                className={twMerge(keyClass, "cursor-pointer")}
                onMouseDown={handleAcceptClick}
              >
                {t("tapHint")}
              </button>
            ) : (
              <span>
                {t.rich("tabHint", {
                  keyButton: () => (
                    <button
                      type="button"
                      className={twMerge(keyClass, "cursor-pointer")}
                      onMouseDown={handleAcceptClick}
                    >
                      Tab
                    </button>
                  ),
                })}
              </span>
            )}
            {showHintControls && (
              <>
                {allHintsResolved ? (
                  <>
                    <span aria-hidden>⋅</span>
                    {`${phantomHintIndex + 1}/${phantomHintSize}`}
                  </>
                ) : null}
                <div className="inline-flex">
                  <button
                    type="button"
                    className="w-[18px] h-[18px] rounded-l-full border border-gray-300 flex items-center justify-center"
                    onClick={() => {
                      doc.editor.hints.findNextPhantomHintIndex(-1);
                    }}
                  >
                    <Icon
                      name="ArrowDown"
                      className="rotate-90 [--color-icon-secondary:var(--input-placeholder)]"
                    />
                  </button>
                  <button
                    type="button"
                    className="w-[18px] h-[18px] rounded-r-full border -ml-[1px] border-gray-300 flex items-center justify-center"
                    onClick={() => {
                      doc.editor.hints.findNextPhantomHintIndex(1);
                    }}
                  >
                    <Icon
                      name="ArrowDown"
                      className="-rotate-90 [--color-icon-secondary:var(--input-placeholder)]"
                    />
                  </button>
                </div>
                <span aria-hidden>⋅</span>
                <span>
                  {t.rich("nextOptionHint", {
                    keyButton: () => (
                      <>
                        <span className={keyClass}>⇧</span> <span className={keyClass}>Tab</span>
                      </>
                    ),
                  })}
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

function PhantomHintWrapperWithIntl(props: PhantomHintWrapperProps) {
  return (
    <NextIntlClientProvider messages={messages} locale="en">
      <PhantomHintWrapper {...props} />
    </NextIntlClientProvider>
  );
}

export { PhantomHintWrapperWithIntl as PhantomHintWrapper };
